(function () {
  'use strict';

  const CART_DRAWER_SELECTOR = 'cart-drawer';
  const MANAGED_UPSELL_SELECTOR = `${CART_DRAWER_SELECTOR} [data-sofyre-managed="true"]`;
  const REFRESH_SELECTORS = ['.cart-drawer__footer', '.cart-progress', '.cart-checkpoints-bar'];
  const SHIPPING_OPT_OUT_KEY = 'sofyre-shipping-protection-opt-out';
  const BUNDLE25_CODE = 'BUNDLE25';
  const BUNDLE25_HANDLES = new Set([
    'critter-control-spray-upsell',
    'mosquito-patches-1',
    'spider-repellent-pouches-6',
  ]);
  const SYNC_DEBOUNCE_MS = 350;
  const SYNC_RETRY_MS = 150;

  let cartCache = null;
  const pendingVariantIds = new Set();
  let shippingProtectionSyncing = false;
  let syncCartStateQueued = false;
  let syncDebounceTimer = null;
  let quantityUpdateUntil = 0;

  window.__cartHasSubscription = window.__cartHasSubscription || false;

  function getRoutes() {
    return window.routes || {};
  }

  let totalsRefreshPromise = null;

  async function refreshCartTotals() {
    if (totalsRefreshPromise) return totalsRefreshPromise;

    totalsRefreshPromise = (async () => {
      try {
        const routes = getRoutes();
        const response = await fetch(`${routes.cart_url || '/cart'}?section_id=cart-drawer`, {
          credentials: 'same-origin',
          cache: 'no-store',
        });
        if (!response.ok) return;

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const cartDrawer = document.querySelector(CART_DRAWER_SELECTOR);
        if (!cartDrawer) return;

        const sourceMoney = doc.querySelectorAll('.cart-drawer__totals__row__money');
        const targetMoney = cartDrawer.querySelectorAll('.cart-drawer__totals__row__money');
        sourceMoney.forEach((sourceEl, index) => {
          if (targetMoney[index]) {
            targetMoney[index].textContent = sourceEl.textContent;
          }
        });

        const sourceCheckoutPrice = doc.querySelector('#CartDrawer-Checkout .cart-drawer__total-price');
        const targetCheckoutPrice = document.querySelector('#CartDrawer-Checkout .cart-drawer__total-price');
        if (sourceCheckoutPrice && targetCheckoutPrice) {
          targetCheckoutPrice.textContent = sourceCheckoutPrice.textContent;
        }

        const sourceItems = doc.querySelector('cart-drawer-items');
        const targetItems = cartDrawer.querySelector('cart-drawer-items');
        if (sourceItems && targetItems && sourceItems.dataset.subtotal) {
          targetItems.dataset.subtotal = sourceItems.dataset.subtotal;
        }

        const sourceTotalSavings = doc.querySelector('.cart-drawer__total-savings');
        const targetTotalSavings = cartDrawer.querySelector('.cart-drawer__total-savings');
        if (sourceTotalSavings && targetTotalSavings) {
          targetTotalSavings.textContent = sourceTotalSavings.textContent;
        }
      } catch (error) {
        console.warn('Cart totals refresh skipped:', error);
      } finally {
        totalsRefreshPromise = null;
      }
    })();

    return totalsRefreshPromise;
  }

  function markQuantityUpdate() {
    quantityUpdateUntil = Date.now() + 2500;
  }

  function isQuantityUpdating() {
    return Date.now() < quantityUpdateUntil;
  }

  function isCartLineItemMutating() {
    const drawerItems = document.querySelector(`${CART_DRAWER_SELECTOR} cart-drawer-items`);
    if (!drawerItems) return false;

    if (drawerItems.classList.contains('loading')) return true;
    if (drawerItems.hasAttribute('aria-busy') && drawerItems.getAttribute('aria-busy') === 'true') {
      return true;
    }
    if (drawerItems.querySelector('.loading-overlay:not(.hidden)')) return true;
    if (drawerItems.querySelector('.cart-drawer-item.loading, .cart-item.loading')) return true;
    if (document.querySelector(`${CART_DRAWER_SELECTOR} cart-remove-button.loading`)) return true;

    return false;
  }

  function scheduleSyncCartState(delay = SYNC_DEBOUNCE_MS) {
    if (pendingVariantIds.size > 0 || isQuantityUpdating()) return;

    clearTimeout(syncDebounceTimer);
    syncDebounceTimer = setTimeout(async () => {
      if (isCartLineItemMutating() || isQuantityUpdating()) {
        scheduleSyncCartState(SYNC_RETRY_MS);
        return;
      }
      await syncCartState();
    }, delay);
  }

  async function fetchCart() {
    const response = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error('Failed to load cart');
    cartCache = await response.json();
    window.__cartHasSubscription = cartCache.items.some(
      (item) => item.selling_plan_allocation != null
    );
    return cartCache;
  }

  function cartHasBundle25Upsell() {
    return (cartCache?.items || []).some((item) =>
      BUNDLE25_HANDLES.has(item.handle || item.product_handle)
    );
  }

  function cartHasBundle25Code() {
    const codes = cartCache?.discount_codes || [];
    if (codes.some((entry) => (entry?.code || '').toUpperCase() === BUNDLE25_CODE)) {
      return true;
    }

    const applications = [
      ...(cartCache?.cart_level_discount_applications || []),
      ...(cartCache?.discount_applications || []),
    ];

    return applications.some((application) => {
      const title = `${application?.title || ''} ${application?.code || ''}`.toUpperCase();
      return title.includes(BUNDLE25_CODE);
    });
  }

  async function updateBundle25Code(code) {
    if (code) {
      await fetch(`/discount/${code}`, {
        credentials: 'same-origin',
        redirect: 'manual',
      });
    } else {
      await fetch('/discount/CLEAR', {
        credentials: 'same-origin',
        redirect: 'manual',
      });
    }

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({ discount: code }),
    });

    if (!response.ok) {
      throw new Error(await response.text());
    }

    await fetchCart();
  }

  async function syncBundle25Discount() {
    if (!cartCache) await fetchCart();

    const hasBundleUpsell = cartHasBundle25Upsell();
    const hasBundleCode = cartHasBundle25Code();

    if (hasBundleUpsell && !hasBundleCode) {
      await updateBundle25Code(BUNDLE25_CODE);
      if (!cartHasBundle25Code()) {
        throw new Error('BUNDLE25 was not applied to the cart');
      }
    } else if (!hasBundleUpsell && hasBundleCode) {
      await updateBundle25Code('');
    }
  }

  function findCartItems(handle, variantId) {
    if (!cartCache) return [];
    const numericVariantId = Number(variantId);
    return cartCache.items.filter(
      (item) => item.variant_id === numericVariantId || (handle && item.handle === handle)
    );
  }

  function findCartItem(handle, variantId) {
    return findCartItems(handle, variantId)[0] || null;
  }

  async function removeCartLineItem(lineKey) {
    const response = await fetch(`${getRoutes().cart_change_url || '/cart/change'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: lineKey, quantity: 0 }),
    });

    if (!response.ok) {
      throw new Error(await response.text());
    }

    return response.json();
  }

  function isShippingProtectionEl(upsellEl) {
    return (
      upsellEl?.classList.contains('shipping-protection-product') ||
      upsellEl?.dataset.shippingProtectionDefault === 'true'
    );
  }

  function getShippingProtectionEl() {
    return document.querySelector(
      `${CART_DRAWER_SELECTOR} .shipping-protection-product[data-sofyre-managed="true"], ${CART_DRAWER_SELECTOR} [data-shipping-protection-default="true"][data-sofyre-managed="true"]`
    );
  }

  function userOptedOutOfShipping() {
    return sessionStorage.getItem(SHIPPING_OPT_OUT_KEY) === 'true';
  }

  function setShippingOptOut(optedOut) {
    if (optedOut) {
      sessionStorage.setItem(SHIPPING_OPT_OUT_KEY, 'true');
    } else {
      sessionStorage.removeItem(SHIPPING_OPT_OUT_KEY);
    }
  }

  function cartHasMainItemsBesides(variantId) {
    if (!cartCache) return false;
    const excludeId = Number(variantId);
    return cartCache.items.some((item) => item.variant_id !== excludeId);
  }

  function cartHasSubscriptionBesides(variantId) {
    if (!cartCache) return false;
    const excludeId = Number(variantId);
    return cartCache.items.some(
      (item) => item.selling_plan_allocation != null && item.variant_id !== excludeId
    );
  }

  function lineItemIsSubscription(lineItem) {
    return lineItem?.selling_plan_allocation != null;
  }

  function isVariantPending(variantId) {
    return pendingVariantIds.has(String(variantId));
  }

  function isUpsellPending(upsellEl) {
    return upsellEl && isVariantPending(upsellEl.dataset.id);
  }

  function setUpsellLoading(variantId, loading) {
    if (!variantId) return;
    const id = String(variantId);

    if (loading) {
      pendingVariantIds.add(id);
    } else {
      pendingVariantIds.delete(id);
    }

    document.querySelectorAll(`${MANAGED_UPSELL_SELECTOR}[data-id="${id}"]`).forEach((el) => {
      el.classList.toggle('upsell--loading', loading);
    });
  }

  function restorePendingLoadingStates() {
    pendingVariantIds.forEach((variantId) => setUpsellLoading(variantId, true));
  }

  function setUpsellSelected(upsellEl, selected) {
    upsellEl.dataset.selected = selected ? 'true' : 'false';
    upsellEl.classList.toggle('upsell--selected', selected);
    upsellEl.setAttribute('aria-checked', selected ? 'true' : 'false');
  }

  function syncTogglesFromCart() {
    document.querySelectorAll(MANAGED_UPSELL_SELECTOR).forEach((upsellEl) => {
      if (isUpsellPending(upsellEl) || isShippingProtectionEl(upsellEl)) return;
      const inCart = !!findCartItem(upsellEl.dataset.handle, upsellEl.dataset.id);
      setUpsellSelected(upsellEl, inCart);
    });
  }

  function updateCartCount() {
    if (!cartCache) return;
    const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden="true"]');
    if (bubble) bubble.textContent = String(cartCache.item_count);
  }

  async function refreshCartDrawerSections() {
    if (isCartLineItemMutating()) return;

    const routes = getRoutes();
    const sectionUrl = `${routes.cart_url || '/cart'}?section_id=cart-drawer`;
    const response = await fetch(sectionUrl, { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) return;

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const sourceDrawer = doc.querySelector(CART_DRAWER_SELECTOR);
    const targetDrawer = document.querySelector(CART_DRAWER_SELECTOR);

    if (sourceDrawer && targetDrawer) {
      const wasOpen =
        targetDrawer.classList.contains('active') || targetDrawer.hasAttribute('open');
      const sourceInner = sourceDrawer.querySelector('.drawer__inner');
      const targetInner = targetDrawer.querySelector('.drawer__inner');
      if (sourceInner && targetInner) {
        targetInner.replaceWith(sourceInner);
      }
      targetDrawer.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
      if (wasOpen) {
        targetDrawer.classList.add('active');
        targetDrawer.setAttribute('open', '');
      }
    } else {
      REFRESH_SELECTORS.forEach((selector) => {
        const source = doc.querySelector(selector);
        const target = document.querySelector(`${CART_DRAWER_SELECTOR} ${selector}`);
        if (source && target) target.replaceWith(source);
      });
    }

    restorePendingLoadingStates();

    try {
      const bubbleResponse = await fetch(`${routes.cart_url || '/cart'}?section_id=cart-icon-bubble`, {
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (bubbleResponse.ok) {
        const bubbleHtml = await bubbleResponse.text();
        const bubbleDoc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
        const sourceBubble = bubbleDoc.querySelector('.shopify-section');
        const targetBubble = document.getElementById('cart-icon-bubble')?.closest('.shopify-section');
        if (sourceBubble && targetBubble) targetBubble.replaceWith(sourceBubble);
      }
    } catch (error) {
      console.warn('Cart icon bubble refresh skipped:', error);
    }
  }

  async function addUpsell(upsellEl) {
    const variantId = Number(upsellEl.dataset.id);
    const sellingPlanId = upsellEl.dataset.sellingplanId;

    if (!cartCache) await fetchCart();

    const shouldUseSubscription = isShippingProtectionEl(upsellEl)
      ? cartHasSubscriptionBesides(variantId)
      : window.__cartHasSubscription;

    const payload = {
      items: [{ id: variantId, quantity: 1 }],
    };

    if (shouldUseSubscription && sellingPlanId) {
      payload.items[0].selling_plan = Number(sellingPlanId);
    }

    const response = await fetch(`${getRoutes().cart_add_url || '/cart/add'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || 'Failed to add upsell');
    }

    return response.json();
  }

  async function removeUpsell(upsellEl) {
    await fetchCart();
    const lineItems = findCartItems(upsellEl.dataset.handle, upsellEl.dataset.id);

    for (const lineItem of lineItems) {
      await removeCartLineItem(lineItem.key);
    }

    if (lineItems.length) {
      await fetchCart();
    }
  }

  async function removeAllShippingProtectionLines(upsellEl) {
    await fetchCart();
    const lineItems = findCartItems(upsellEl.dataset.handle, upsellEl.dataset.id);

    for (const lineItem of lineItems) {
      await removeCartLineItem(lineItem.key);
    }

    if (lineItems.length) {
      await fetchCart();
    }
  }

  async function addShippingProtectionLine(upsellEl, shouldBeSubscription) {
    const variantId = Number(upsellEl.dataset.id);
    const sellingPlanId = upsellEl.dataset.sellingplanId;
    const payload = {
      items: [{ id: variantId, quantity: 1 }],
    };

    if (shouldBeSubscription && sellingPlanId) {
      payload.items[0].selling_plan = Number(sellingPlanId);
    }

    const response = await fetch(`${getRoutes().cart_add_url || '/cart/add'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(await response.text());
    }

    return response.json();
  }

  async function setShippingProtectionInCart(upsellEl, shouldBeSubscription) {
    await removeAllShippingProtectionLines(upsellEl);
    await addShippingProtectionLine(upsellEl, shouldBeSubscription);
    await fetchCart();
  }

  async function dedupeShippingProtectionItems(upsellEl, variantId, sellingPlanId) {
    const lineItems = findCartItems(upsellEl.dataset.handle, variantId);
    if (lineItems.length <= 1) return lineItems[0] || null;

    const shouldBeSubscription = cartHasSubscriptionBesides(variantId) && !!sellingPlanId;
    const preferred =
      lineItems.find((item) => lineItemIsSubscription(item) === shouldBeSubscription) || lineItems[0];

    for (const item of lineItems) {
      if (item.key === preferred.key) continue;
      await removeCartLineItem(item.key);
    }

    await fetchCart();
    return findCartItem(upsellEl.dataset.handle, variantId);
  }

  async function ensureShippingProtection() {
    if (isCartLineItemMutating() || isQuantityUpdating() || shippingProtectionSyncing) return;

    const upsellEl = getShippingProtectionEl();
    if (!upsellEl || isUpsellPending(upsellEl)) return;

    const variantId = Number(upsellEl.dataset.id);
    const sellingPlanId = upsellEl.dataset.sellingplanId;
    if (!variantId) return;

    if (!cartCache) await fetchCart();

    if (cartCache.item_count === 0) {
      setShippingOptOut(false);
      setUpsellSelected(upsellEl, true);
      return;
    }

    if (!cartHasMainItemsBesides(variantId)) {
      setUpsellSelected(upsellEl, !userOptedOutOfShipping());
      return;
    }

    if (userOptedOutOfShipping()) {
      setUpsellSelected(upsellEl, false);
      shippingProtectionSyncing = true;
      try {
        await removeAllShippingProtectionLines(upsellEl);
      } finally {
        shippingProtectionSyncing = false;
      }
      return;
    }

    setUpsellSelected(upsellEl, true);

    const shouldBeSubscription = cartHasSubscriptionBesides(variantId) && !!sellingPlanId;
    const lineItems = findCartItems(upsellEl.dataset.handle, variantId);
    const hasCorrectSingleLine =
      lineItems.length === 1 &&
      lineItemIsSubscription(lineItems[0]) === shouldBeSubscription;

    if (hasCorrectSingleLine) return;

    shippingProtectionSyncing = true;
    setUpsellLoading(variantId, true);

    try {
      await setShippingProtectionInCart(upsellEl, shouldBeSubscription);
      await refreshCartTotals();
      updateCartCount();
    } catch (error) {
      console.error('Shipping protection sync failed:', error);
      setUpsellSelected(upsellEl, true);
    } finally {
      setUpsellLoading(variantId, false);
      shippingProtectionSyncing = false;
    }
  }

  async function syncCartState() {
    if (syncCartStateQueued || isCartLineItemMutating() || isQuantityUpdating()) {
      if (isCartLineItemMutating() || isQuantityUpdating()) scheduleSyncCartState(SYNC_RETRY_MS);
      return;
    }
    syncCartStateQueued = true;

    try {
      await fetchCart();
      await syncBundle25Discount();
      syncTogglesFromCart();
      await ensureShippingProtection();
      await refreshCartTotals();
      updateCartCount();
    } finally {
      syncCartStateQueued = false;
    }
  }

  async function handleToggle(upsellEl, turnOn) {
    const variantId = upsellEl.dataset.id;
    if (isVariantPending(variantId)) return;

    if (isShippingProtectionEl(upsellEl)) {
      setShippingOptOut(!turnOn);
    }

    setUpsellLoading(variantId, true);

    const previousState = upsellEl.dataset.selected === 'true';
    const previousOptOut = userOptedOutOfShipping();
    setUpsellSelected(upsellEl, turnOn);

    try {
      if (turnOn) {
        await addUpsell(upsellEl);
      } else {
        await removeUpsell(upsellEl);
      }

      await fetchCart();
      await syncBundle25Discount();
      await refreshCartTotals();
      updateCartCount();
      await refreshCartDrawerSections();
      restorePendingLoadingStates();
      await syncCartState();
      restorePendingLoadingStates();
    } catch (error) {
      console.error('Cart drawer upsell toggle failed:', error);
      setUpsellSelected(upsellEl, previousState);
      setShippingOptOut(previousOptOut);
      await fetchCart();
      syncTogglesFromCart();
      await ensureShippingProtection();
    } finally {
      setUpsellLoading(variantId, false);
    }
  }

  function blockNativeManagedUpsellForms() {
    document
      .querySelectorAll(`${MANAGED_UPSELL_SELECTOR} product-form[data-is-cart-upsell="true"]`)
      .forEach((form) => {
        form.removeAttribute('data-is-cart-upsell');
      });
  }

  function onToggleClick(event) {
    const cartDrawer = document.querySelector(CART_DRAWER_SELECTOR);
    if (!cartDrawer || !cartDrawer.contains(event.target)) return;

    const addButton = event.target.closest('.upsell__add-btn');
    if (addButton) {
      const upsellEl = addButton.closest('[data-sofyre-managed="true"]');
      if (!upsellEl || isShippingProtectionEl(upsellEl)) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (!isVariantPending(upsellEl.dataset.id)) {
        handleToggle(upsellEl, true);
      }
      return;
    }

    const toggleTarget = event.target.closest('.upsell-toggle-btn');
    if (!toggleTarget) return;

    const upsellEl = toggleTarget.closest('[data-sofyre-managed="true"]');
    if (!upsellEl || upsellEl.dataset.toggle !== 'true') return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const turnOn = upsellEl.dataset.selected !== 'true';
    handleToggle(upsellEl, turnOn);
  }

  function watchCartCountChanges() {
    const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden="true"]');
    if (!bubble) return;

    const observer = new MutationObserver(() => {
      scheduleSyncCartState();
    });

    observer.observe(bubble, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  }

  function watchQuantityUpdates() {
    const cartDrawer = document.querySelector(CART_DRAWER_SELECTOR);
    if (!cartDrawer) return;

    cartDrawer.addEventListener(
      'click',
      (event) => {
        if (event.target.closest('.quantity__button, .quantity__input')) {
          markQuantityUpdate();
        }
      },
      true
    );

    cartDrawer.addEventListener(
      'change',
      (event) => {
        if (!event.target.matches('.quantity__input')) return;
        const qty = Number(event.target.value);
        if (!Number.isFinite(qty) || qty <= 0) return;
        markQuantityUpdate();
        scheduleSyncCartState(2600);
      },
      true
    );
  }

  function watchCartDrawerItemMutations() {
    const drawerItems = document.querySelector(`${CART_DRAWER_SELECTOR} cart-drawer-items`);
    if (!drawerItems) return;

    const observer = new MutationObserver(() => {
      if (isQuantityUpdating() || isCartLineItemMutating()) return;
      scheduleSyncCartState(SYNC_RETRY_MS);
    });

    observer.observe(drawerItems, {
      childList: true,
    });
  }

  async function init() {
    blockNativeManagedUpsellForms();
    await syncCartState();
    document.addEventListener('click', onToggleClick, true);
    watchQuantityUpdates();
    watchCartCountChanges();
    watchCartDrawerItemMutations();

    window.addEventListener('pageshow', () => syncCartState());
    document.addEventListener('cart-drawer:opened', () => {
      blockNativeManagedUpsellForms();
      scheduleSyncCartState(0);
    });

    const cartDrawer = document.querySelector(CART_DRAWER_SELECTOR);
    if (cartDrawer) {
      const drawerObserver = new MutationObserver(() => {
        if (cartDrawer.classList.contains('active')) {
          blockNativeManagedUpsellForms();
          scheduleSyncCartState(0);
        }
      });
      drawerObserver.observe(cartDrawer, { attributes: true, attributeFilter: ['class'], childList: true, subtree: true });
    }

    window.SofyreCartUpsells = {
      syncCartState,
      ensureShippingProtection,
      dedupeShippingProtectionItems,
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
